/**
 * Navigator i18n foundation, structurally aligned with Monolithos:
 * five UiLocale values, namespace dictionaries, English type base, related
 * Chinese fallback, reactive useT(), interpolation and host-locale projection.
 *
 * UI locale is deliberately independent from diagnosis/content language.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import enShell from './en/shell'
import enEntry from './en/entry'
import enMemory from './en/memory'
import enAction from './en/action'
import enAttention from './en/attention'
import enAirlock from './en/airlock'
import enAirlockActivation from './en/airlockActivation'
import enControl from './en/control'
import enOnboarding from './en/onboarding'
import enPresence from './en/presence'
import enResearch from './en/research'
import enResults from './en/results'
import enShared from './en/shared'
import jaShell from './ja/shell'
import jaEntry from './ja/entry'
import jaMemory from './ja/memory'
import jaAction from './ja/action'
import jaAttention from './ja/attention'
import jaAirlock from './ja/airlock'
import jaAirlockActivation from './ja/airlockActivation'
import jaControl from './ja/control'
import jaOnboarding from './ja/onboarding'
import jaPresence from './ja/presence'
import jaResearch from './ja/research'
import jaResults from './ja/results'
import jaShared from './ja/shared'
import koShell from './ko/shell'
import koEntry from './ko/entry'
import koMemory from './ko/memory'
import koAction from './ko/action'
import koAttention from './ko/attention'
import koAirlock from './ko/airlock'
import koAirlockActivation from './ko/airlockActivation'
import koControl from './ko/control'
import koOnboarding from './ko/onboarding'
import koPresence from './ko/presence'
import koResearch from './ko/research'
import koResults from './ko/results'
import koShared from './ko/shared'
import zhCNShell from './zh-CN/shell'
import zhCNEntry from './zh-CN/entry'
import zhCNMemory from './zh-CN/memory'
import zhCNAction from './zh-CN/action'
import zhCNAttention from './zh-CN/attention'
import zhCNAirlock from './zh-CN/airlock'
import zhCNAirlockActivation from './zh-CN/airlockActivation'
import zhCNControl from './zh-CN/control'
import zhCNOnboarding from './zh-CN/onboarding'
import zhCNPresence from './zh-CN/presence'
import zhCNResearch from './zh-CN/research'
import zhCNResults from './zh-CN/results'
import zhCNShared from './zh-CN/shared'
import zhTWShell from './zh-TW/shell'
import zhTWEntry from './zh-TW/entry'
import zhTWMemory from './zh-TW/memory'
import zhTWAction from './zh-TW/action'
import zhTWAttention from './zh-TW/attention'
import zhTWAirlock from './zh-TW/airlock'
import zhTWAirlockActivation from './zh-TW/airlockActivation'
import zhTWControl from './zh-TW/control'
import zhTWOnboarding from './zh-TW/onboarding'
import zhTWPresence from './zh-TW/presence'
import zhTWResearch from './zh-TW/research'
import zhTWResults from './zh-TW/results'
import zhTWShared from './zh-TW/shared'

export const UI_LOCALES = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko'] as const
export type UiLocale = (typeof UI_LOCALES)[number]
export type UiLocalePreference = 'system' | UiLocale

export const UI_LOCALE_STORAGE_KEY = 'navigator.uiLocale.preference.v1'
export const MONOLITHOS_LOCALE_EVENT = 'monolithos:ui-locale-changed'

type EnglishDictionary = {
  shell: typeof enShell
  entry: typeof enEntry
  memory: typeof enMemory
  action: typeof enAction
  attention: typeof enAttention
  airlock: typeof enAirlock
  airlockActivation: typeof enAirlockActivation
  control: typeof enControl
  onboarding: typeof enOnboarding
  presence: typeof enPresence
  research: typeof enResearch
  results: typeof enResults
  shared: typeof enShared
}
type WidenLeaves<T> = T extends string
  ? string
  : T extends Record<string, unknown>
    ? { [K in keyof T]: WidenLeaves<T[K]> }
    : T
type LocaleDictionary = WidenLeaves<EnglishDictionary>
type DeepPartial<T> = T extends string
  ? string
  : T extends Record<string, unknown>
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T

const dictionaries: Record<UiLocale, DeepPartial<LocaleDictionary>> = {
  en: { shell: enShell, entry: enEntry, memory: enMemory, action: enAction, attention: enAttention, airlock: enAirlock, airlockActivation: enAirlockActivation, control: enControl, onboarding: enOnboarding, presence: enPresence, research: enResearch, results: enResults, shared: enShared },
  'zh-CN': { shell: zhCNShell, entry: zhCNEntry, memory: zhCNMemory, action: zhCNAction, attention: zhCNAttention, airlock: zhCNAirlock, airlockActivation: zhCNAirlockActivation, control: zhCNControl, onboarding: zhCNOnboarding, presence: zhCNPresence, research: zhCNResearch, results: zhCNResults, shared: zhCNShared },
  'zh-TW': { shell: zhTWShell, entry: zhTWEntry, memory: zhTWMemory, action: zhTWAction, attention: zhTWAttention, airlock: zhTWAirlock, airlockActivation: zhTWAirlockActivation, control: zhTWControl, onboarding: zhTWOnboarding, presence: zhTWPresence, research: zhTWResearch, results: zhTWResults, shared: zhTWShared },
  ja: { shell: jaShell, entry: jaEntry, memory: jaMemory, action: jaAction, attention: jaAttention, airlock: jaAirlock, airlockActivation: jaAirlockActivation, control: jaControl, onboarding: jaOnboarding, presence: jaPresence, research: jaResearch, results: jaResults, shared: jaShared },
  ko: { shell: koShell, entry: koEntry, memory: koMemory, action: koAction, attention: koAttention, airlock: koAirlock, airlockActivation: koAirlockActivation, control: koControl, onboarding: koOnboarding, presence: koPresence, research: koResearch, results: koResults, shared: koShared },
}

export const FALLBACK_CHAINS: Record<UiLocale, readonly UiLocale[]> = {
  en: ['en'],
  'zh-CN': ['zh-CN', 'en'],
  'zh-TW': ['zh-TW', 'zh-CN', 'en'],
  ja: ['ja', 'en'],
  ko: ['ko', 'en'],
}

declare global {
  interface Window {
    __monolithos_currentLocale?: () => string
  }
}

export function isUiLocale(value: unknown): value is UiLocale {
  return typeof value === 'string' && (UI_LOCALES as readonly string[]).includes(value)
}

export function isUiLocalePreference(value: unknown): value is UiLocalePreference {
  return value === 'system' || isUiLocale(value)
}

/** BCP-47 projection copied from the sealed Monolithos behavior. */
export function mapLanguageTag(value: string | undefined): UiLocale {
  if (!value) return 'en'
  const lower = value.toLowerCase()
  if (
    lower.startsWith('zh-hant') ||
    lower === 'zh-tw' ||
    lower === 'zh-hk' ||
    lower === 'zh-mo' ||
    lower.startsWith('zh-tw-')
  ) return 'zh-TW'
  if (
    lower.startsWith('zh-hans') ||
    lower === 'zh-cn' ||
    lower === 'zh-sg' ||
    lower.startsWith('zh-cn-') ||
    lower === 'zh'
  ) return 'zh-CN'
  if (lower === 'zh-hant') return 'zh-TW'
  if (lower.startsWith('en')) return 'en'
  if (lower.startsWith('ja')) return 'ja'
  if (lower.startsWith('ko')) return 'ko'
  return 'en'
}

export function readStoredLocalePreference(
  storage?: Pick<Storage, 'getItem'>,
): UiLocalePreference {
  try {
    const target = storage ?? (typeof window === 'undefined' ? undefined : window.localStorage)
    const value = target?.getItem(UI_LOCALE_STORAGE_KEY)
    return isUiLocalePreference(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

export function persistLocalePreference(
  preference: UiLocalePreference,
  storage?: Pick<Storage, 'setItem' | 'removeItem'>,
): void {
  try {
    const target = storage ?? (typeof window === 'undefined' ? undefined : window.localStorage)
    if (!target) return
    if (preference === 'system') target.removeItem(UI_LOCALE_STORAGE_KEY)
    else target.setItem(UI_LOCALE_STORAGE_KEY, preference)
  } catch {
    // A blocked store never makes the current session unusable.
  }
}

export function readMonolithosHostLocale(target?: Window): UiLocale | undefined {
  try {
    const value = target?.__monolithos_currentLocale?.()
    return isUiLocale(value) ? value : value ? mapLanguageTag(value) : undefined
  } catch {
    return undefined
  }
}

export function resolveUiLocale(
  preference: UiLocalePreference,
  hostLocale: UiLocale | undefined,
  browserLanguage: string | undefined,
): UiLocale {
  if (preference !== 'system') return preference
  return hostLocale ?? mapLanguageTag(browserLanguage)
}

function getNested(value: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') return undefined
    return (current as Record<string, unknown>)[segment]
  }, value)
}

export function interpolate(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    const value = params[key]
    return value === undefined ? placeholder : String(value)
  })
}

export function translate(
  locale: UiLocale,
  key: string,
  params?: Record<string, string | number>,
): string {
  for (const candidate of FALLBACK_CHAINS[locale]) {
    const value = getNested(dictionaries[candidate], key)
    if (typeof value === 'string') return interpolate(value, params)
  }
  return key.split('.').at(-1) ?? key
}

interface I18nContextValue {
  locale: UiLocale
  preference: UiLocalePreference
  setPreference: (preference: UiLocalePreference) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export interface I18nProviderProps {
  children: ReactNode
  hostLocale?: UiLocale
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

export function I18nProvider({ children, hostLocale, storage }: I18nProviderProps) {
  const [preference, setPreferenceState] = useState<UiLocalePreference>(() =>
    readStoredLocalePreference(storage),
  )
  const [projectedHostLocale, setProjectedHostLocale] = useState<UiLocale | undefined>(() =>
    hostLocale ?? (typeof window === 'undefined' ? undefined : readMonolithosHostLocale(window)),
  )
  const [browserLanguage, setBrowserLanguage] = useState<string | undefined>(() =>
    typeof navigator === 'undefined' ? undefined : navigator.language,
  )

  useEffect(() => {
    if (hostLocale) setProjectedHostLocale(hostLocale)
  }, [hostLocale])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onHostLocale = (event: Event) => {
      const detail = (event as CustomEvent<{ locale?: string }>).detail
      if (isUiLocale(detail?.locale)) setProjectedHostLocale(detail.locale)
    }
    const onBrowserLanguage = () => setBrowserLanguage(navigator.language)
    window.addEventListener(MONOLITHOS_LOCALE_EVENT, onHostLocale)
    window.addEventListener('languagechange', onBrowserLanguage)
    return () => {
      window.removeEventListener(MONOLITHOS_LOCALE_EVENT, onHostLocale)
      window.removeEventListener('languagechange', onBrowserLanguage)
    }
  }, [])

  const locale = resolveUiLocale(preference, projectedHostLocale, browserLanguage)

  useLayoutEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dataset.uiLocale = locale
  }, [locale])

  const setPreference = useCallback((next: UiLocalePreference) => {
    persistLocalePreference(next, storage)
    setPreferenceState(next)
  }, [storage])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(locale, key, params),
    [locale],
  )

  const value = useMemo<I18nContextValue>(
    () => ({ locale, preference, setPreference, t }),
    [locale, preference, setPreference, t],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside I18nProvider')
  return value
}

export function useT(): I18nContextValue['t'] {
  return useI18n().t
}

/**
 * Shared primitives also render in isolated tests and print-only entrypoints.
 * In the mounted app they inherit the live provider; outside it they use a
 * deterministic locale instead of crashing or silently hard-coding Chinese.
 */
export function useOptionalT(fallbackLocale: UiLocale = 'zh-CN'): I18nContextValue['t'] {
  const value = useContext(I18nContext)
  return value?.t ?? ((key, params) => translate(fallbackLocale, key, params))
}

export const __testing__ = { dictionaries, getNested }
